import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

import { ObjectException } from 'global/errors';
import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import services from 'api/services';

const { FreecodeCreatorService } = services;

export default function useCreatorSaveFunction(updateServerError) {
  const { setTest, setTestImpl, test, testImpl } = useFreecodeContextData();
  const { setCodeSol, updateRunResponse } = useFreecodeContextRun();

  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (onError, skippResetRun = false) => {
      try {
        if (saving) return toast.error('Already saving');
        setSaving(true);

        if (!test?._id) throw new ObjectException(`Test to save not found`, 'VALIDATION_ERROR');
        if (!testImpl?._id) throw new ObjectException(`Implementation to save not found`, 'VALIDATION_ERROR');

        const request = {
          test: { ...test },
          testImpl: { ...testImpl },
        };
        if (!testImpl.enableSkeletonEditing) delete request.testImpl.generated;

        const response = await FreecodeCreatorService.saveFreecode(test._id, testImpl._id, request);

        if (response.error)
          throw new ObjectException(response.error?.message || 'Save freecode error', 'RESPONSE_ERROR');
        if (!response?.test?._id) throw new ObjectException('Test missing from response', 'RESPONSE_ERROR');
        if (!response?.testImpl?._id)
          throw new ObjectException('Implementation missing from response', 'RESPONSE_ERROR');
        if (response?.test?._id !== response?.testImpl?.test)
          throw new ObjectException('Test and implementation misssmatch', 'RESPONSE_ERROR');

        if (response.test._id !== test._id) throw new ObjectException('Test save misssmatch', 'RESPONSE_ERROR');
        if (response.testImpl._id !== testImpl._id)
          throw new ObjectException('Implementation save misssmatch', 'RESPONSE_ERROR');

        setTest((oldTest) => ({ ...oldTest, ...response.test }));
        setTestImpl((oldTestImpl) => ({ ...oldTestImpl, ...response.testImpl }));
        setCodeSol(
          response.testImpl.solution ||
            testImpl.solution ||
            response.testImpl.generated?.skeletons?.python ||
            testImpl.generated?.skeletons?.python ||
            'Code not found...'
        );
        if (!skippResetRun) updateRunResponse(undefined);
        toast.success('Save done');
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message?.error || e?.message || 'Could not save');
        updateServerError(e?.message?.response?.serverError);
        if (onError) onError();
      } finally {
        setSaving(false);
      }
    },
    [saving, setCodeSol, setTest, setTestImpl, test, testImpl, updateRunResponse, updateServerError]
  );

  return { save, saving };
}
